// The map-label style object (spec §3) and its resolver. Pure: no React, no store.

export const LABEL_SHAPES = ['none', 'pill', 'rect'];
export const COLOR_MODES = ['fixed', 'status', 'variable'];
export const MAX_LABEL_FIELDS = 2;
export const MAX_RANGES = 3;
/** Label text size presets → badge/plain text px; 'normal' is the original 11 px. */
export const CROWDING_MODES = ['spread', 'stack', 'group'];
export const TEXT_SIZES = ['small', 'normal', 'large', 'xlarge'];
export const TEXT_SIZE_PX = Object.freeze({ small: 9, normal: 11, large: 14, xlarge: 17 });
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export const DEFAULT_LABEL_STYLE = Object.freeze({
  shape: 'none',
  bg: '#ffffff',
  bgOpacity: 0.9,
  fg: '#334155',
  fields: [],
  colorMode: 'fixed',
  allowOverlap: false,
  crowding: 'spread',
  clickSelects: false,
  selectionGlow: true,
  shrinkWhenZoomedOut: false,
  showFieldLabels: true,
  singleLine: false,
  compact: false,
  textSize: 'normal',
  minZoom: 12,
});

const hex = (v, fallback) => (typeof v === 'string' && HEX_RE.test(v) ? v.toLowerCase() : fallback);

function normalizeVariable(raw) {
  if (!raw || typeof raw !== 'object' || typeof raw.key !== 'string' || !raw.key.trim()) return null;
  const out = { key: raw.key.trim().slice(0, 64), noDataColor: hex(raw.noDataColor, '#94a3b8') };
  if (typeof raw.onColor === 'string' || typeof raw.offColor === 'string') {
    out.onColor = hex(raw.onColor, '#10b981');
    out.offColor = hex(raw.offColor, '#64748b');
  }
  const ranges = Array.isArray(raw.ranges) ? raw.ranges : [];
  const clean = ranges
    .filter((r) => r && typeof r === 'object' && (r.to === null || (typeof r.to === 'number' && Number.isFinite(r.to))))
    .map((r) => ({ to: r.to, color: hex(r.color, '#94a3b8') }))
    .slice(0, MAX_RANGES)
    .sort((a, b) => (a.to === null ? 1 : b.to === null ? -1 : a.to - b.to))
    .filter((r, i, arr) => (r.to === null ? i === arr.length - 1 : i === 0 || r.to !== arr[i - 1].to));
  if (clean.length && clean[clean.length - 1].to !== null) clean[clean.length - 1] = { ...clean[clean.length - 1], to: null };
  out.ranges = clean;
  if (!out.ranges.length && out.onColor === undefined) return null;
  return out;
}

/** Tolerant reader: junk → null, string → parsed, missing keys → defaults. */
export function normalizeLabelStyle(raw) {
  let v = raw;
  if (typeof v === 'string') { try { v = JSON.parse(v); } catch { return null; } }
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const fields = Array.isArray(v.fields)
    ? [...new Set(v.fields.filter((f) => typeof f === 'string' && f.trim()).map((f) => f.trim().slice(0, 64)))].slice(0, MAX_LABEL_FIELDS)
    : [];
  const opacity = typeof v.bgOpacity === 'number' && Number.isFinite(v.bgOpacity) ? Math.min(1, Math.max(0.2, v.bgOpacity)) : DEFAULT_LABEL_STYLE.bgOpacity;
  const style = {
    shape: LABEL_SHAPES.includes(v.shape) ? v.shape : DEFAULT_LABEL_STYLE.shape,
    bg: hex(v.bg, DEFAULT_LABEL_STYLE.bg),
    bgOpacity: opacity,
    fg: hex(v.fg, DEFAULT_LABEL_STYLE.fg),
    fields,
    colorMode: COLOR_MODES.includes(v.colorMode) ? v.colorMode : DEFAULT_LABEL_STYLE.colorMode,
    allowOverlap: v.allowOverlap === true,
    crowding: CROWDING_MODES.includes(v.crowding) ? v.crowding : (v.allowOverlap === true ? 'stack' : 'spread'),
    clickSelects: v.clickSelects === true,
    selectionGlow: v.selectionGlow !== false,
    shrinkWhenZoomedOut: v.shrinkWhenZoomedOut === true,
    showFieldLabels: v.showFieldLabels !== false,
    singleLine: v.singleLine === true,
    compact: v.compact === true,
    textSize: TEXT_SIZES.includes(v.textSize) ? v.textSize : DEFAULT_LABEL_STYLE.textSize,
    minZoom: normalizeMinZoom(v.minZoom),
  };
  if (style.colorMode === 'variable') {
    const variable = normalizeVariable(v.variable);
    if (variable) style.variable = variable; else style.colorMode = 'fixed';
  }
  return style;
}

export const MIN_ZOOM_FLOOR = 6;
export const MIN_ZOOM_CEIL = 16;
function normalizeMinZoom(v) {
  // Numbers only: null / '' / false coerce to 0 and would clamp to the floor.
  if (typeof v !== 'number' || !Number.isFinite(v)) return DEFAULT_LABEL_STYLE.minZoom;
  return Math.min(MIN_ZOOM_CEIL, Math.max(MIN_ZOOM_FLOOR, Math.round(v)));
}

const MAX_HOPS = 16;

/** device → nearest ancestor group with a style → org → default. */
export function resolveLabelStyle(device, groupsById, orgSettings) {
  const own = normalizeLabelStyle(device?.attributes?.mapLabelStyle);
  if (own) return { style: own, source: 'device' };
  let gid = Number(device?.groupId) || 0;
  const seen = new Set();
  for (let hop = 0; gid && hop < MAX_HOPS && !seen.has(gid); hop += 1) {
    seen.add(gid);
    const g = groupsById?.[gid];
    if (!g) break;
    const s = normalizeLabelStyle(g.attributes?.mapLabelStyle);
    if (s) return { style: s, source: 'group', groupId: gid };
    gid = Number(g.groupId) || 0;
  }
  const org = normalizeLabelStyle(orgSettings?.mapLabelStyle);
  if (org) return { style: org, source: 'org' };
  return { style: DEFAULT_LABEL_STYLE, source: 'default' };
}

/** Crowding is decided by the org style only; group and vehicle styles never override it. */
export function resolveCrowding(orgSettings) {
  return normalizeLabelStyle(orgSettings?.mapLabelStyle)?.crowding ?? 'spread';
}

/** Whether a click on a vehicle's label (badge, plain text, card or fan label) selects
 *  the vehicle, like a click on its marker. Org style only, default off. */
export function resolveLabelClickSelects(orgSettings) {
  return normalizeLabelStyle(orgSettings?.mapLabelStyle)?.clickSelects === true;
}

export function buildLabelStyleIndex(devices, groupsById, orgSettings) {
  const out = new Map();
  for (const d of Object.values(devices || {})) out.set(d.id, resolveLabelStyle(d, groupsById, orgSettings));
  return out;
}
